import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'applications/common/routing/outlet-context.js';
import { useSelector } from 'react-redux';

import MojitoServices from 'mojito/services';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import MojitoCore from 'mojito/core';

import { convertToMenuLink } from 'applications/sports/pages/sport-page/views/utils.js';
import { useListItems, useEventGroups } from 'modules/common/hooks';

const { useLocalizedParams, useScrollIntoHashElement } = MojitoPresentation.Hooks;
const { module: SportGroupsList, types: SportGroupsListTypes } = MojitoModules.SportGroupsList;
const eventGroupsSelectors = MojitoServices.EventGroups.selectors;
const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const { MenuLinkPromotions, PrebuiltPromotionCards } = MojitoModules.Promotions;
const { FlexPane } = MojitoPresentation.Components;
const PromotionTypes = MojitoServices.Promotions.types;
const { COUPONS } = MojitoModules.SingleLeagueCoupon.SingleLeagueCouponTypes;
const { Context: AnalyticsContext, ContextBuilder: AnalyticsContextBuilder } =
    MojitoCore.Presentation.AnalyticsContext;
const AnalyticsTypes = MojitoServices.Analytics.types;
const { COMPETITIONS_PAGE } = AnalyticsTypes.SOURCE;
const selectionSourceContext = new AnalyticsContextBuilder()
    .withAnalyticsPath(COMPETITIONS_PAGE)
    .build();

export default function CompetitionsView({ mojitoTools }) {
    const { config, appContext, emitPerformanceMark, instanceId } = mojitoTools;
    const { analyticsEmitter, subscriptionResolver } = appContext;
    const { sportId } = useLocalizedParams();
    const contentInfo = useOutletContext();

    const groupIds = useListItems(subscriptionResolver.cmsCouponPrefix(sportId));
    const eventGroups = useEventGroups(groupIds, instanceId);
    const eventGroupNames = useSelector(state => eventGroupsSelectors.selectEventGroupNames(state));
    const cmsCouponLinks = groupIds
        ?.filter(id => eventGroups[id])
        .map(eventGroupId => ({ id: eventGroupId, name: eventGroupNames[eventGroupId] }))
        .filter(group => !!group.name)
        .map(coupon => convertToMenuLink(coupon, sportId, appContext.routeResolver));

    const onLinkClick = useCallback(
        item => {
            const { isCMSCoupon, data } = item;
            const type = isCMSCoupon ? 'cmsCouponsClicked' : 'cmsQuickLinksClicked';
            analyticsEmitter.emitAnalytics(type, data);
        },
        [analyticsEmitter]
    );

    const [allLeaguesLoaded, setAllLeaguesLoaded] = useState(false);
    const [promotionsLoaded, setPromotionsLoaded] = useState(false);

    useScrollIntoHashElement(allLeaguesLoaded, config.scrollOffset);

    useEffect(() => {
        emitPerformanceMark('moduleRendered', allLeaguesLoaded && promotionsLoaded);
    }, [emitPerformanceMark, allLeaguesLoaded, promotionsLoaded]);

    const onPromotionsLoaded = useCallback(() => setPromotionsLoaded(true), []);

    return (
        <FlexPane config={config.container} class="ta-CompetitionsView">
            <EventCardsCarousel
                class="ta-CompetitionsEventCardsCarousel"
                sportId={sportId}
                pageId={sportId}
                config={config.eventCardsCarousel}
            />
            <MenuLinkPromotions
                class="ta-CompetitionsMenuLinkPromotions"
                config={config.menuLinkPromotions}
                extraLinks={cmsCouponLinks}
                applicablePage={sportId}
                locationId={PromotionTypes.LOCATION.QUICK_LINKS}
                onLinkClick={onLinkClick}
                onDataLoad={onPromotionsLoaded}
            />
            <SportGroupsList
                class="ta-CompetitionsTopLeagues"
                config={config.topLeagues}
                sportId={sportId}
                type={SportGroupsListTypes.TYPE.TOP}
                groupId={subscriptionResolver.topLeagues(sportId)}
                pageType={COUPONS.COMPETITIONS}
            />
            <AnalyticsContext.Provider value={selectionSourceContext}>
                <PrebuiltPromotionCards
                    class="ta-CompetitionsPrebuiltPromotionCards"
                    applicablePage={sportId}
                    config={config.prebuiltPromotionCards}
                />
            </AnalyticsContext.Provider>
            <SportGroupsList
                class="ta-CompetitionsAllLeagues"
                config={config.allLeagues}
                sportId={sportId}
                type={SportGroupsListTypes.TYPE.ALL}
                groupId={contentInfo?.competitions?.group}
                pageType={COUPONS.COMPETITIONS}
                onDataLoad={() => {
                    setAllLeaguesLoaded(true);
                }}
            />
        </FlexPane>
    );
}
